import { defineStore, acceptHMRUpdate } from "pinia";
import { computed, ref } from "vue";
import Cookies from "js-cookie";
import client from "@/api/openapi-ts";
import router from "@/router";
import { removeFromArray, updateInArray } from "./helpers/helpers";
import { usePlugins } from "./helpers/usePlugins";
import { PaginationArgs, PaginatedData } from "@/types/api/common";
import { Entity } from "@/types/api/common";

import type { components } from "@/api/schema.d";

type schemas = components["schemas"];
type UserProfile = components["schemas"]["UserProfile"];

type AuthResponse = {
  authentication_result?: { access_token: string; refresh_token: string };
  challenge_name?: string;
  session?: string;
  challenge_parameters?: { USER_ID_FOR_SRP: string };
  platform_auth?: { token: string };
};

type PendoVisitor = {
  visitor: {
    id: number;
    email: string;
    full_name: string;
    roles: string[];
    has_tunnl_theme: boolean;
    has_deeproot_theme: boolean;
  };
  account?: { id: number | string; name: string };
};

/** True when missing, non-numeric, or id is not in the given member list. */
function membershipCookieInvalid(
  raw: string | undefined,
  members: { id: number }[] | undefined,
): boolean {
  if (!raw) return true;
  const id = Number(raw);
  return !Number.isFinite(id) || !(members?.some((m) => m.id === id) ?? false);
}

/** App client that shares a name with the given org (post-migration collision). */
function appClientIdMatchingOrgName(
  payload: Pick<UserProfile, "orgs" | "app_clients">,
  orgId: number,
): number | undefined {
  const org = payload.orgs?.find((o) => o.id === orgId);
  if (!org) return;
  return payload.app_clients?.find((c) => c.name === org.name)?.id;
}

/**
 * Resolve mutually exclusive org / app_client cookies from membership + defaults.
 * Prefers default app client over default org, and prefers an app client over a
 * same-named org (post-migration).
 */
function resolveMembershipSelection(payload: UserProfile): {
  orgId?: number;
  appClientId?: number;
} {
  const orgIdRaw = Cookies.get("org_id");
  const appClientIdRaw = Cookies.get("app_client_id");
  const orgCookieInvalid = membershipCookieInvalid(orgIdRaw, payload.orgs);
  const appClientCookieInvalid = membershipCookieInvalid(
    appClientIdRaw,
    payload.app_clients,
  );
  const {
    default_org_id: defaultOrgId,
    default_app_client_id: defaultAppClientId,
  } = payload;

  let orgId: number | undefined = !orgCookieInvalid
    ? Number(orgIdRaw)
    : undefined;
  let appClientId: number | undefined = !appClientCookieInvalid
    ? Number(appClientIdRaw)
    : undefined;

  const noValidOrgOrAppClientCookie =
    orgCookieInvalid && appClientCookieInvalid;

  if (noValidOrgOrAppClientCookie && defaultAppClientId) {
    appClientId = defaultAppClientId;
  } else if (noValidOrgOrAppClientCookie && defaultOrgId) {
    orgId = defaultOrgId;
  } else if (
    orgCookieInvalid &&
    defaultOrgId &&
    (!appClientIdRaw || appClientCookieInvalid)
  ) {
    orgId = defaultOrgId;
  } else if (
    appClientCookieInvalid &&
    defaultAppClientId &&
    (!orgIdRaw || orgCookieInvalid)
  ) {
    appClientId = defaultAppClientId;
  }

  // Dual cookies are a stale state (independent setters). Prefer app client.
  if (orgId !== undefined && appClientId !== undefined) {
    orgId = undefined;
  }

  // Prefer app client over same-named org (post-migration).
  if (orgId !== undefined) {
    const collidingAppClientId = appClientIdMatchingOrgName(payload, orgId);
    if (collidingAppClientId !== undefined) {
      orgId = undefined;
      appClientId = collidingAppClientId;
    }
  }

  return { orgId, appClientId };
}

function applyMembershipCookies(selection: {
  orgId?: number;
  appClientId?: number;
}) {
  if (selection.orgId !== undefined) {
    Cookies.set("org_id", selection.orgId.toString());
    Cookies.remove("app_client_id");
  } else if (selection.appClientId !== undefined) {
    Cookies.set("app_client_id", selection.appClientId.toString());
    Cookies.remove("org_id");
  } else {
    Cookies.remove("org_id");
    Cookies.remove("app_client_id");
  }
}

export const useUserStore = defineStore("user", () => {
  const { $ws } = usePlugins();

  const loading = ref(false);
  const saving = ref(false);
  const initializing = ref(false);
  const initialized = ref(false);
  const resettingPassword = ref(false);
  const resettingUser = ref(false);
  const authResponse = ref<AuthResponse>();
  const currentUser = ref<UserProfile>();
  const profileUpdated = ref<string>();
  const users = ref<UserProfile[]>([]);
  const paginatedUsers = ref<PaginatedData<UserProfile>>({});
  const usersForCsv = ref<UserProfile[]>();
  const metaAudiences = ref<Entity[]>([]);
  const orgIdRefresh = ref(0);
  const appClientIdRefresh = ref(0);
  const loadingImpersonatedUser = ref<number | undefined>(undefined);
  const loadingAudiences = ref(false);
  const loadingUsersByRole = ref(false);
  /** In-memory cache for getUsersByRole; list is stable within a session. */
  const usersByRoleCache = new Map<number, UserProfile[]>();
  const usersByRoleInFlight = new Map<number, Promise<UserProfile[]>>();

  const addUser = (payload: UserProfile) => {
    if (!users.value.find((u) => u.id === payload.id)) {
      (users.value as UserProfile[]).push(payload);
    }
  };

  const setCurrentUser = (payload?: UserProfile) => {
    currentUser.value = payload;

    if (payload) {
      applyMembershipCookies(resolveMembershipSelection(payload));
      orgIdRefresh.value++;
      appClientIdRefresh.value++;

      window._loq = window._loq || [];
      window._loq.push([
        "custom",
        { name: payload.name, email: payload.email },
      ]);
    }
  };
  const currentOrg = computed(() => {
    // force recompute b/c cookies are not reactive
    // eslint-disable-next-line @typescript-eslint/no-unused-expressions
    orgIdRefresh.value; // Access the value to trigger reactivity
    const org = currentUser.value?.orgs?.find(
      (o) => o.id === Number(Cookies.get("org_id")),
    );
    if (!org) return undefined;
    return { ...org, meta_audiences: metaAudiences.value };
  });

  const currentAppClient = computed(() => {
    // force recompute b/c cookies are not reactive
    // eslint-disable-next-line @typescript-eslint/no-unused-expressions
    appClientIdRefresh.value; // Access the value to trigger reactivity
    const appClient = currentUser.value?.app_clients?.find(
      (c) => c.id === Number(Cookies.get("app_client_id")),
    );
    if (!appClient) return undefined;
    return { ...appClient, meta_audiences: metaAudiences.value };
  });

  const setCurrentOrg = (org_id?: number) => {
    orgIdRefresh.value++;
    if (org_id) {
      Cookies.set("org_id", org_id.toString());
      initializePendo();
    } else {
      Cookies.remove("org_id");
    }
  };

  const setCurrentAppClient = (app_client_id?: number) => {
    appClientIdRefresh.value++;
    if (app_client_id) {
      Cookies.set("app_client_id", app_client_id.toString());
      initializePendo();
    } else {
      Cookies.remove("app_client_id");
    }
  };

  const initializePendo = () => {
    if (!window.pendo || Cookies.get("impersonating")) return;
    const userOrgs = currentUser.value?.orgs ?? [];
    const hasTunnlTheme = userOrgs.some((org) => org.theme?.name === "tunnl");
    const hasDeepRootTheme = userOrgs.some(
      (org) => org.theme?.name === "deeproot",
    );

    const pendoVisitor = <PendoVisitor>{
      visitor: {
        id: currentUser.value!.id,
        email: currentUser.value?.email,
        full_name: currentUser.value?.name,
        roles: currentUser.value?.roles?.map((r) => r.name) ?? [],
        has_tunnl_theme: hasTunnlTheme,
        has_deeproot_theme: hasDeepRootTheme,
      },
    };

    if (currentOrg.value) {
      pendoVisitor.account = {
        id: currentOrg.value?.id,
        name: currentOrg.value?.name,
      };
    } else if (currentAppClient.value) {
      pendoVisitor.account = {
        id: `ac-${currentAppClient.value?.id}`,
        name: currentAppClient.value?.name,
      };
    }

    if (window.pendo.visitorId) {
      window.pendo.identify(pendoVisitor);
    } else {
      window.pendo.initialize(pendoVisitor);
    }
  };

  const setAccessToken = (payload?: string) => {
    if (payload) {
      Cookies.set("access_token", payload);
    } else {
      Cookies.remove("access_token");
    }
  };

  const setRefreshToken = (payload?: string) => {
    if (payload) {
      Cookies.set("refresh_token", payload);
    } else {
      Cookies.remove("refresh_token");
    }
  };

  const setImpersonating = (payload?: number) => {
    setCurrentUser(undefined);
    if (payload) {
      Cookies.set("impersonating", payload.toString());
    } else {
      Cookies.remove("impersonating");
    }

    if ($ws) {
      $ws.close();
      $ws.initialize(new Set(["NotificationChannel"]));
    }
  };

  const redirectToLogin = (): false => {
    setAccessToken(undefined);
    setRefreshToken(undefined);
    setCurrentUser(undefined);
    initialized.value = false;

    if (typeof window !== "undefined") {
      const from = window.location.hash.replace(/^#\//, "").trim();
      if (!/^login.*$/.test(from) && !/^signup.*$/.test(from)) {
        router.push({ name: "login", query: { from } }).catch(() => {});
      }
    }
    return false;
  };

  const fetchOrgMetaAudiences = async (org_id: number) => {
    loadingAudiences.value = true;
    try {
      const { data: metaAudienceData } = await client.GET(
        "/api/v1/orgs/{org_id}/meta_audiences",
        {
          params: {
            path: { org_id },
            query: {
              meta_audience_fields: [
                "id",
                "name",
                "audience_type_id",
                "audience_type",
                "client_id",
                "premium",
                "long_description",
                "audience_issues",
                "created_by_id",
                "shared_with",
                "audience_category",
                "audience_category_id",
                "audience_topic",
                "audience_topic_id",
              ],
            } as never,
          },
        },
      );
      metaAudiences.value = (metaAudienceData as unknown as Entity[]) ?? [];
    } catch {
      // Profile auth succeeded; don't clear the session on audience load failure.
    } finally {
      loadingAudiences.value = false;
    }
  };

  const loadUserProfile = async (): Promise<UserProfile | false> => {
    const { data, response } = await client.GET("/api/v1/users/profile");
    if (response.status === 504) {
      router.push({ name: "maintenance" }).catch(() => {});
      return false;
    }
    if (response.status === 401) {
      redirectToLogin();
      return false;
    }
    if (!response.ok || !data) {
      return false;
    }
    return data;
  };

  const getCurrentUser = async (): Promise<boolean | void> => {
    loading.value = true;
    try {
      const data = await loadUserProfile();
      if (data === false) {
        return false;
      }
      setCurrentUser(data);

      const org_id = Cookies.get("org_id");
      // Clear before replace so a failed fetch cannot keep a prior org's data.
      metaAudiences.value = [];
      if (org_id) {
        await fetchOrgMetaAudiences(Number(org_id));
      }
    } catch {
      // Network failure: keep the session, block navigation.
      return false;
    } finally {
      loading.value = false;
    }
  };

  return {
    // -------------- Getters + Setters --------------
    loading,
    saving,
    initializing,
    initialized,
    resettingPassword,
    resettingUser,
    authResponse,
    currentUser,
    profileUpdated,
    users,
    paginatedUsers,
    usersForCsv,
    loadingImpersonatedUser,
    loadingAudiences,
    loadingUsersByRole,

    // -------------- Computed --------------
    currentOrg,
    currentAppClient,

    // -------------- Complex Setters --------------
    setCurrentOrg,
    setCurrentAppClient,
    initializePendo,
    setImpersonating,

    // -------------- Actions --------------
    redirectToLogin,
    getCurrentUser,
    login: async (user: { email: string; password: string }) => {
      loading.value = true;
      try {
        const { data, error } = await client.POST("/api/v1/users/login", {
          body: { user } as never,
        });
        if (error) throw error;
        const authData = data as AuthResponse | undefined;
        if (authData?.authentication_result) {
          setAccessToken(authData.authentication_result.access_token);
          setRefreshToken(authData.authentication_result.refresh_token);
          // Login always applies the user's configured default, so drop leftover
          // org / app_client cookies before the profile fetch. Otherwise a stale
          // Org-Id makes the first (and only) profile org-scoped, with no products.
          applyMembershipCookies({});
          await getCurrentUser();
          Cookies.remove("impersonating");
          $ws.initialize(new Set(["NotificationChannel"]));
        } else {
          authResponse.value = authData;
        }
      } finally {
        loading.value = false;
      }
    },
    respondToAuthChallenge: async (
      challengeResponse: {
        type: string;
        session: string;
        responses: { NEW_PASSWORD: string; USERNAME: string };
      },
      platformAuth: { token: string },
    ) => {
      loading.value = true;
      try {
        const { data, error } = await client.POST(
          "/api/v1/users/respond_to_auth_challenge",
          {
            body: {
              challenge_response: challengeResponse,
              platform_auth: platformAuth,
            } as never,
          },
        );
        if (error) throw error;
        const authData = data as AuthResponse | undefined;
        if (authData?.authentication_result) {
          setAccessToken(authData.authentication_result.access_token);
          setRefreshToken(authData.authentication_result.refresh_token);
          applyMembershipCookies({});
          await getCurrentUser();
        } else {
          authResponse.value = authData;
        }
      } finally {
        loading.value = false;
      }
    },
    forgotPassword: async (email: string) => {
      loading.value = true;
      try {
        const { data, error } = await client.POST(
          "/api/v1/users/forgot_password",
          {
            body: { email } as never,
          },
        );
        if (error) throw error;
        return data;
      } finally {
        loading.value = false;
      }
    },
    resetForgotPassword: async (args: { token: string; password: string }) => {
      loading.value = true;
      try {
        const { data, error } = await client.POST(
          "/api/v1/users/reset_forgot_password",
          {
            body: {
              token: args.token,
              password: args.password,
            } as never,
          },
        );
        if (error) throw error;
        return data as { email?: string } | undefined;
      } finally {
        loading.value = false;
      }
    },
    getUser: async (user: UserProfile) => {
      const { data, error, response } = await client.GET(
        "/api/v1/users/{user_id}",
        {
          params: { path: { user_id: user.id } },
        },
      );
      return { data: data as unknown as UserProfile, error, response };
    },
    getUsersByRole: async (role_ids: number) => {
      const cached = usersByRoleCache.get(role_ids);
      if (cached !== undefined) {
        return cached;
      }
      const inflightExisting = usersByRoleInFlight.get(role_ids);
      if (inflightExisting) {
        return inflightExisting;
      }

      let resolveInflight!: (users: UserProfile[]) => void;
      let rejectInflight!: (reason?: unknown) => void;
      const inflight = new Promise<UserProfile[]>((resolve, reject) => {
        resolveInflight = resolve;
        rejectInflight = reject;
      });
      usersByRoleInFlight.set(role_ids, inflight);

      loadingUsersByRole.value = true;
      client
        .GET("/api/v1/users", {
          params: {
            query: {
              role_ids: role_ids,
              include_inactive: false,
            } as never,
          },
        })
        .then(({ data }) => {
          const list = (data as unknown as UserProfile[]) ?? [];
          usersByRoleCache.set(role_ids, list);
          resolveInflight(list);
        })
        .catch((err) => {
          rejectInflight(err);
        })
        .finally(() => {
          loadingUsersByRole.value = false;
          usersByRoleInFlight.delete(role_ids);
        });

      return inflight;
    },
    getCachedUsersByRole: (role_ids: number) =>
      usersByRoleCache.get(role_ids) ?? [],
    index: async (include_inactive = false) => {
      loading.value = true;
      try {
        const { data } = await client.GET("/api/v1/active_users", {
          params: { query: { include_inactive } as never },
        });
        users.value = (data as unknown as UserProfile[]) ?? [];
      } finally {
        loading.value = false;
      }
    },
    indexPaginated: async (
      include_inactive = false,
      for_csv = false,
      args: PaginationArgs,
    ) => {
      loading.value = true;
      try {
        const { data, error, response } = await client.GET("/api/v1/users", {
          params: { query: { include_inactive, ...args } as never },
        });
        if (error) {
          throw error;
        }
        if (for_csv) {
          usersForCsv.value = (data as unknown as UserProfile[]) ?? [];
        } else {
          paginatedUsers.value = {
            pagination: {
              page: Number(response.headers.get("current-page")),
              page_items: Number(response.headers.get("page-items")),
              total_pages: Number(response.headers.get("total-pages")),
              total_items: Number(response.headers.get("total-count")),
            },
            data: (data as unknown as UserProfile[]) ?? [],
            args: args,
          };
        }
      } finally {
        loading.value = false;
      }
    },
    orgUsers: async (params: { user_fields: string[] }) => {
      loading.value = true;
      try {
        // The backend reads this array query param as `user_fields[]` (the
        // bracketed form axios used to send) with no non-bracket fallback, so
        // send the bracketed key explicitly rather than openapi-fetch's default
        // `user_fields=...` serialization.
        const { data } = await client.GET("/api/v1/users/for_org", {
          params: { query: { "user_fields[]": params.user_fields } as never },
        });
        users.value = (data as unknown as UserProfile[]) ?? [];
      } finally {
        loading.value = false;
      }
    },
    create: async (user: Partial<UserProfile>) => {
      saving.value = true;
      try {
        const { data } = await client.POST("/api/v1/users", {
          body: user as never,
        });
        const created = data as unknown as UserProfile;
        addUser(created);
        return created;
      } finally {
        saving.value = false;
      }
    },
    update: async (user: UserProfile) => {
      saving.value = true;
      try {
        const { data } = await client.PUT("/api/v1/users/{user_id}", {
          params: { path: { user_id: user.id } },
          body: user as never,
        });
        const updated = data as unknown as UserProfile;
        updateInArray(users.value as UserProfile[], updated);
        return updated;
      } finally {
        saving.value = false;
      }
    },
    bulkUpdatePermissions: async (
      appClientId: number,
      userIds: number[],
      product_ids: number[],
      meta_audience_category_ids: number[],
      meta_audience_ids: number[],
      tableau_workbook_ids: number[],
    ) => {
      saving.value = true;
      try {
        await client.PUT("/api/v1/users/permissions", {
          body: {
            app_client_id: appClientId,
            user_ids: userIds,
            product_ids: product_ids,
            meta_audience_category_ids: meta_audience_category_ids,
            meta_audience_ids: meta_audience_ids,
            tableau_workbook_ids: tableau_workbook_ids,
          },
        });
      } finally {
        saving.value = false;
      }
    },
    addToOrg: async (user: UserProfile) => {
      saving.value = true;
      try {
        const { data } = await client.PUT(
          "/api/v1/users/{user_id}/add_to_org",
          {
            params: { path: { user_id: user.id } },
          },
        );
        addUser(data as unknown as UserProfile);
      } finally {
        saving.value = false;
      }
    },
    removeFromOrg: async (user: UserProfile) => {
      saving.value = true;
      try {
        await client.DELETE("/api/v1/users/{user_id}/remove_from_org", {
          params: { path: { user_id: user.id } },
        });
        removeFromArray(users.value as UserProfile[], user.id);
      } finally {
        saving.value = false;
      }
    },
    delete: async (user: UserProfile) => {
      saving.value = true;
      try {
        await client.DELETE("/api/v1/users/{user_id}", {
          params: { path: { user_id: user.id } },
        });
        removeFromArray(users.value as UserProfile[], user.id);
      } finally {
        saving.value = false;
      }
    },
    exists: async (email: string) => {
      loading.value = true;
      try {
        const { data, error } = await client.POST("/api/v1/users/exists", {
          body: { email },
        });
        if (error) throw error;
        return { data };
      } finally {
        loading.value = false;
      }
    },
    logout: async () => {
      loading.value = true;
      try {
        await client.POST("/api/v1/users/logout");
      } finally {
        if ($ws) {
          $ws.close();
        }
        setCurrentUser(undefined);
        setCurrentOrg(undefined);
        setCurrentAppClient(undefined);
        setAccessToken(undefined);
        setRefreshToken(undefined);
        setImpersonating(undefined);
        initialized.value = false;
        loading.value = false;
      }
    },
    updateProfile: async (user: UserProfile) => {
      saving.value = true;
      profileUpdated.value = undefined;
      try {
        const { data, error } = await client.PUT(
          "/api/v1/users/update_profile",
          {
            body: user as unknown as schemas["UserUpdateProfile"],
          },
        );
        if (error) {
          profileUpdated.value = "error";
          return;
        }
        currentUser.value = data;
        profileUpdated.value = "success";
      } catch {
        profileUpdated.value = "error";
      } finally {
        saving.value = false;
      }
    },
    resetPassword: async (user: UserProfile) => {
      resettingPassword.value = true;
      try {
        const { data, error } = await client.PUT(
          "/api/v1/users/reset_password/{user_id}",
          {
            params: { path: { user_id: user.id } },
          },
        );
        if (error) {
          throw new Error(
            error.message || "Could not reset the user's password.",
          );
        }
        if (!data?.password) {
          throw new Error(
            "Password reset did not return a temporary password.",
          );
        }
        return { password: data.password };
      } finally {
        resettingPassword.value = false;
      }
    },
  };
});

if (import.meta.hot) {
  import.meta.hot.accept(acceptHMRUpdate(useUserStore, import.meta.hot));
}
